import { camelizeKeys } from 'humps'
import { getEnv, onPatch, types } from 'mobx-state-tree'
import qs from 'qs'

import { byName } from '../utils/helpers'
import { findAProviderConfigFor } from './FindAProviderConfigModel'
import { LocationStore } from './LocationStore'
import { PracticeStore } from './PracticeStore'
import { ProviderStore } from './ProviderStore'
import {
  PARTNER,
  PROVIDER_DETAILS,
  RESOLVE_PROVIDER_LOCATION,
  SPECIALTIES,
} from './queries'
import { RootStoreBase } from './RootStore.base'
import { SearchStore } from './SearchStore'
import { SpecialtyModel } from './SpecialtyModel'

export const RootStore = RootStoreBase.props({
  page: '/',
  loading: false,
  staticPage: false,
  errorMessage: '',
  partnerId: '',
  practiceId: '',
  locationId: '',
  providerId: '',
  source: '',
  locationStore: types.optional(LocationStore, {}),
  providerStore: types.optional(ProviderStore, {}),
  practiceStore: types.optional(PracticeStore, {}),
  searchStore: types.optional(SearchStore, {}),
  specialties: types.optional(types.array(SpecialtyModel), []),
  showConfirmDialog: types.optional(types.boolean, false),
  mapZoom: types.optional(types.number, 5),
  mapCenter: types.optional(
    types.model('MapCenter', {
      lat: types.number,
      lng: types.number,
    }),
    { lat: 39.8283, lng: -98.5795 }
  ),
  boundingBox: types.frozen({
    south: 25.82,
    west: -124.39,
    north: 49.38,
    east: -66.94,
  }),
  attributionData: types.optional(
    types.model('attributionData', {
      utmSource: types.maybeNull(types.string),
      utmMedium: types.maybeNull(types.string),
      utmCampaign: types.maybeNull(types.string),
      utmTerm: types.maybeNull(types.string),
      utmContent: types.maybeNull(types.string),
      gclid: types.maybeNull(types.string),
      fbclid: types.maybeNull(types.string),
      msclkid: types.maybeNull(types.string),
      twclid: types.maybeNull(types.string),
      ttclid: types.maybeNull(types.string),
      liFatId: types.maybeNull(types.string),
    }),
    {}
  ),
})
  .views(self => ({
    get currentUrl() {
      switch (self.page) {
        case 'providerLocation':
          return `/providers/${self.providerId}/locations/${self.locationId}`
        case 'practiceLocation':
          return `/practice/${self.practiceId}/locations/${self.locationId}/`
        case 'practice':
          return `/practice/${self.practiceId}/`
        default:
          return '/'
      }
    },
    get specialtyFieldEnabled() {
      return findAProviderConfigFor(self.partner).isSpecialtyFilterEnabled
    },
    get hasSpecialties() {
      return self.specialties.length > 0
    },
    get sortedSpecialties() {
      return self.specialties.slice().sort(byName)
    },
    get filteredSpecialties() {
      const uniqueSpecialties = self.sortedSpecialties.filter(
        (specialty, index, specialties) =>
          specialties.findIndex(item => item.name === specialty.name) === index
      )
      const allowedSpecialties = findAProviderConfigFor(
        self.partner
      ).allowedSpecialties
      if (!allowedSpecialties || allowedSpecialties.length === 0) {
        return uniqueSpecialties
      }
      return uniqueSpecialties.filter(s => allowedSpecialties.includes(s.name))
    },
    get selectedSpecialty() {
      const current = self.searchStore.filteredSpecialty
      if (
        current === '' ||
        self.filteredSpecialties.some(s => s.name === current)
      ) {
        return current
      }
      return ''
    },
    get defaultSpecialty() {
      const specialty = findAProviderConfigFor(self.partner).defaultSpecialty
      if (!specialty || !self.specialtyFieldEnabled) {
        return ''
      }
      return self.filteredSpecialties.some(s => s.name === specialty)
        ? specialty
        : ''
    },
    get loadAllLocationsButtonEnabled() {
      return Boolean(
        self.partner &&
          self.partner.findAProviderConfig.isLoadAllLocationsButtonEnabled
      )
    },
    get partner() {
      if (self.partnerId) {
        return self.partners.get(self.partnerId)
      }
      return null
    },
    get modalOpen() {
      return self.locationId !== '' || self.providerId !== ''
    },
    get canShowAllLocations() {
      return (
        self.partner &&
        self.partner.findAProviderConfig.isLoadAllLocationsEnabled &&
        self.searchStore.searchStarted
      )
    },
    get shouldDrawerBeOpen() {
      if (!self.searchStore.searchStarted && !self.locationStore.hasLocations) {
        return false
      }

      if (!self.partner) {
        return true
      }

      return !self.partner.findAProviderConfig.isLoadAllLocationsEnabled
    },
    get partnerTiers() {
      return Array.from(self.providers.values())
        .filter(provider => provider.partnerTier)
        .map(provider => provider.partnerTier)
    },
    get showResults() {
      if (
        self.partnerId &&
        self.partner &&
        self.partner.findAProviderConfig.isShowProviderResultsEnabled
      ) {
        return 'providers'
      }
      return 'locations'
    },
    get showOnlyClaimedAccountsEnabled() {
      return (
        findAProviderConfigFor(self.partner)
          .isShowOnlyClaimedAccountsEnabled === true
      )
    },
    /**
     * @description Gets a location model from the list of loaded
     * locations by locationId. This only works when locations are
     * loaded ahead of time. This will not automatically load a
     * location by provided id.
     *
     * @returns {Location|null} LocationModel
     */
    get selectedLocation() {
      if (self.locationId) {
        return self.locations.get(self.locationId)
      }
      return null
    },
    get canShowResultsPanel() {
      return self.searchStore.searchStarted || self.locationStore.hasLocations
    },
  }))
  .actions(self => {
    let partnerLoadPromise
    const providerLocationErrorMessage = error => {
      const graphQLErrorCode = error?.graphQLErrors?.[0]?.extensions?.code
      const message = error?.message || error?.graphQLErrors?.[0]?.message || ''
      const status =
        error?.response?.status ||
        error?.status ||
        error?.networkError?.statusCode ||
        error?.graphQLErrors?.[0]?.extensions?.status

      if (
        status === 404 ||
        status === '404' ||
        graphQLErrorCode === 'NOT_FOUND' ||
        /not found|inactive mapping|invalid partner/i.test(message)
      ) {
        return 'Provider location not found'
      }

      return 'Unable to load booking information'
    }

    return {
      afterCreate() {
        const {
          partnerId,
          providerLocationId,
          providerLocationInternalId,
          staticPage,
          source,
          utmSource,
          utmMedium,
          utmCampaign,
          utmTerm,
          utmContent,
          gclid,
          fbclid,
          msclkid,
          twclid,
          ttclid,
          liFatId,
        } = camelizeKeys(
          qs.parse(window.location.search, {
            ignoreQueryPrefix: true,
          })
        )
        if (staticPage) {
          self.staticPage = Boolean(staticPage)
        }
        if (source) {
          self.source = source
        }
        if (partnerId) {
          self.partnerId = partnerId
          self.fetchPartner()
        }
        const resolvedProviderLocationId =
          providerLocationInternalId || providerLocationId
        if (resolvedProviderLocationId && partnerId) {
          self.resolveProviderLocationId(resolvedProviderLocationId, partnerId)
        }

        // Extract and store UTM parameters and ad platform click IDs
        if (
          utmSource ||
          utmMedium ||
          utmCampaign ||
          utmTerm ||
          utmContent ||
          gclid ||
          fbclid ||
          msclkid ||
          twclid ||
          ttclid ||
          liFatId
        ) {
          self.attributionData = {
            utmSource: utmSource || null,
            utmMedium: utmMedium || null,
            utmCampaign: utmCampaign || null,
            utmTerm: utmTerm || null,
            utmContent: utmContent || null,
            gclid: gclid || null,
            fbclid: fbclid || null,
            msclkid: msclkid || null,
            twclid: twclid || null,
            ttclid: ttclid || null,
            liFatId: liFatId || null,
          }
          self.debug(
            `Tracking metadata captured: ${JSON.stringify(self.attributionData)}`
          )
        }

        self.fetchSpecialties()

        onPatch(self, patch => {
          switch (patch.path) {
            case '/locationId':
              if (patch.value !== '') {
                self.locationStore.maybeFetchLocation(patch.value)
              }
              break
            case '/practiceId':
              if (patch.value !== '') {
                self.practiceStore.maybeFetchPractice(patch.value)
              }
              break
            default:
          }
        })
      },
      fetchPartner() {
        if (!self.partnerId || self.staticPage) {
          return Promise.resolve(null)
        }

        if (self.partner) {
          return Promise.resolve(self.partner)
        }

        if (partnerLoadPromise) {
          return partnerLoadPromise
        }

        const query = self.query(PARTNER, { id: self.partnerId })

        if (query.loading) {
          self.debug(`LOADING PARTNER CONFIG FOR ${self.partnerId}`)
        }

        partnerLoadPromise = query
          .then(() => {
            if (self.partner.findAProviderConfig.isLoadAllLocationsEnabled) {
              self.partner.fetchAllLocations()
            }
            self.maybeAutoSelectSpecialty()
            return self.partner
          })
          .catch(error => {
            getEnv(self).logger.error(error)
            throw error
          })
          .finally(() => {
            partnerLoadPromise = undefined
          })

        return partnerLoadPromise
      },
      fetchSpecialties() {
        const variables = self.partnerId
          ? { partnerId: self.partnerId }
          : undefined

        self.query(SPECIALTIES, variables).then(
          response => {
            self.setSpecialties(response.specialties)
          },
          error => {
            getEnv(self).logger.error(error)
          }
        )
      },
      async resolveProviderLocationId(providerLocationId, partnerId) {
        try {
          const result = await self.query(RESOLVE_PROVIDER_LOCATION, {
            providerLocationId,
            partnerId,
          })

          const { location, provider } = result.providerLocationMapping

          if (provider?.id) {
            self.openProviderLocation(provider.id, location.id)
          } else {
            self.openPracticeLocation(location.practice.id, location.id)
          }
        } catch (error) {
          self.setErrorMessage(providerLocationErrorMessage(error))
          getEnv(self).logger.error(error)
        }
      },
      async fetchProviderDetails(providerId) {
        const result = await self.query(PROVIDER_DETAILS, {
          id: providerId,
          partnerId: self.partnerId || undefined,
        })

        return result.provider
      },
      setMapCenter(
        bounds = {
          south: 25.82,
          west: -124.39,
          north: 49.38,
          east: -66.94,
        }
      ) {
        self.boundingBox = bounds
      },
      setSpecialties(specialties) {
        self.specialties = specialties
        self.maybeAutoSelectSpecialty()
      },
      maybeAutoSelectSpecialty() {
        const specialty = self.defaultSpecialty

        if (!specialty || self.searchStore.filteredSpecialty) {
          return
        }

        self.searchStore.setSpecialty(specialty)
      },
      openRoot() {
        self.page = '/'
        self.practiceId = ''
        self.providerId = ''
        self.locationId = ''
      },
      openPractice(practiceId) {
        self.page = 'practice'
        self.practiceId = practiceId
        self.locationId = ''
        self.providerId = ''
      },
      openPracticeLocation(practiceId, locationId) {
        self.page = 'practiceLocation'
        self.practiceId = practiceId
        self.locationId = locationId
      },
      openProviderLocation(providerId, locationId) {
        self.page = 'providerLocation'
        self.providerId = providerId
        self.locationId = locationId
      },
      modalClose() {
        if (self.staticPage) {
          return self.openPractice(self.practiceId)
        }
        return self.openRoot()
      },
      openConfirmDialog() {
        self.showConfirmDialog = true
      },
      closeConfirmDialog() {
        self.showConfirmDialog = false
      },
      clearLocations() {
        self.locations.clear()
      },
      clearProviders() {
        self.providers.clear()
      },
      setLoading(value) {
        self.loading = value
      },
      resetErrorMessage() {
        self.errorMessage = ''
      },
      setErrorMessage(message) {
        self.errorMessage = message
      },
      debug(debug) {
        getEnv(self).logger.debug(debug)
      },
      error(error) {
        getEnv(self).logger.error(error)
      },
      showAllLocations() {
        if (self.locationStore.locations.length === 0) {
          self.partner.maybeFetchAllLocations()
        }

        if (self.locationStore.locations.length > 0) {
          const bounds = {
            south: Math.min(
              ...self.locationStore.locations.map(l => l.latNumber)
            ),
            west: Math.min(
              ...self.locationStore.locations.map(l => l.lngNumber)
            ),
            north: Math.max(
              ...self.locationStore.locations.map(l => l.latNumber)
            ),
            east: Math.max(
              ...self.locationStore.locations.map(l => l.lngNumber)
            ),
          }
          self.setMapCenter(bounds)
        } else {
          self.setMapCenter()
        }
      },
      setLocationId(locationId) {
        self.locationId = locationId
      },
    }
  })
